/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/button-has-type */
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react';
import { Swipeable } from 'react-swipeable';
import PropTypes from 'prop-types';

/**
 * This is a simple single item carousel component with fade transition effect.
 * Supports swiping, has automatic loop.
 *
 * Usage:
 *
 *  <SingleItemFadeCarousel
 *   loop
 *   loopDelay={3000}
 *  >
 *    <div>slide1</div>
 *    <div>slide2</div>
 *    <div>slide3</div>
 *  </SingleItemFadeCarousel>
 *
 *
 * You can call next() and prev() methods from parent component, just add ref to
 * SingleItemFadeCarousel.
 *
 * @param {bool} loop - turn on automatic looping
 * @param {int} loopDelay - delay between slide change in loop
 */
const SingleItemFadeCarousel = forwardRef(
  ({ children: slides, loop, loopDelay }, ref) => {
    const [state, setState] = useState({
      indexes: [0, 0],
      firstTop: true,
      blockLoop: false
    });
    const [initIntervalId, setInitIntervalId] = useState(null);

    const [blockTimeout, setBlockTimeout] = useState(null);

    const getPrevIndex = idx => {
      return idx === 0 ? slides.length - 1 : idx - 1;
    };

    const getNextIndex = idx => {
      return idx === slides.length - 1 ? 0 : idx + 1;
    };

    const updateClearTimeout = () => {
      clearTimeout(blockTimeout);
      const timeout = setTimeout(
        () => setState(prevState => ({ ...prevState, blockLoop: false })),
        2000
      );
      setBlockTimeout(timeout);
    };

    const changeSlide = ({ direction, isLoop }) => {
      const getNext = direction === 'next' ? getNextIndex : getPrevIndex;
      setState(oldState => {
        let { indexes } = oldState;
        if (oldState.block) {
          return oldState;
        }
        if (isLoop && oldState.blockLoop) return oldState;
        if (oldState.firstTop) {
          indexes = [indexes[0], getNext(indexes[0])];
        } else {
          indexes = [getNext(indexes[1]), indexes[1]];
        }
        return {
          ...oldState,
          firstTop: !oldState.firstTop,
          indexes,
          blockLoop: !isLoop // block loop if change was made by user
        };
      });
      if (!isLoop) {
        // clear block if change was made by user, after timeout
        updateClearTimeout();
      }
    };

    const changeSlidePrev = () => {
      if (slides.length < 2) return;
      changeSlide({ direction: 'prev' });
      clearInterval(initIntervalId);
    };

    const changeSlideNext = () => {
      if (slides.length < 2) return;
      changeSlide({ direction: 'next' });
      clearInterval(initIntervalId);
    };

    useImperativeHandle(ref, () => ({
      prev() {
        changeSlidePrev();
      },
      next() {
        changeSlideNext();
      }
    }));

    const handleMouseEnter = () => {
      if (!loop || slides.length < 2) return;
      setState(prevState => ({ ...prevState, blockLoop: true }));
    };

    const handleMouseLeave = () => {
      if (!loop || slides.length < 2) return;
      updateClearTimeout();
    };

    useEffect(() => {
      if (!loop || slides.length < 2) return;
      const looped = setInterval(
        () => changeSlide({ direction: 'next' }),
        loopDelay
      );
      setInitIntervalId(looped);
      //  eslint-disable-next-line consistent-return
      return () => {
        clearInterval(looped);
      };
    }, []);

    return (
      <div
        className="single-item-fade-carousel"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={`slides ${state.firstTop ? 'firstTop' : 'secondTop'}`}>
          <Swipeable
            delta={10}
            trackTouch
            trackMouse
            preventDefaultTouchmoveEvent
          >
            <div className="slide1 slide">{slides[state.indexes[0]]}</div>
            <div className="slide2 slide">{slides[state.indexes[1]]}</div>
          </Swipeable>
        </div>
      </div>
    );
  }
);

export default SingleItemFadeCarousel;

SingleItemFadeCarousel.defaultProps = {
  loop: true,
  loopDelay: 4500
};

SingleItemFadeCarousel.propTypes = {
  loop: PropTypes.bool,
  loopDelay: PropTypes.number
};
